// Threads, and the polling loop — TODO T30 (1 Sep 2026); the calls are T17's.
//
// The thread query is the only read in the brain that is not a plain fetch. Each call asks for
// what has arrived since the cursor it already holds, and `mergeThread` appends it — so a quiet
// thread costs one empty page every four seconds no matter how long the conversation is — and
// since T107b a thread with activity in the last half hour also gets those rows again, so a
// transcript or a download that lands after a bubble was drawn reaches it (thread.ts). Reading
// the previous value out of the cache inside `queryFn` is what makes it a delta; the alternative,
// refetching the thread, is the thing the BFF's cursor exists to avoid (services/bff/README.md
// § Polling).
//
// TWO WRITES IN THIS FILE, both at the foot. The PAUSE (T19): `holds`, which the worker reads at
// send time. And WRITING INTO A THREAD (T19a): `outbound_requests`, a request the worker's sweep
// sends on kadmon's number — never the browser, never the BFF — so the send is the second write in
// this app that POLLS for its answer (api/candidateEntries.ts was the first), and the bubble
// arrives through the thread poll when the message has actually left.
import { useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';
import type {
  ConversationSummary,
  FollowUpOffer,
  FollowUpOfferResponse,
  GateState,
  HeldWords,
  HeldWordsResponse,
  MeetingAsk,
  MeetingAskResponse,
  TeamQuestion,
  TeamQuestionsResponse,
  MessageFileResponse,
  MessagePage,
  OutboundRequestRow,
  OutboundRequestsResponse,
  PartyType,
  ThreadHoldState,
  WaitingResponse,
  WaitingThread,
} from '@vera/bff';
import { API_PREFIX, query, type RequestFn } from './core.ts';
import { POLL_MS } from './intervals.ts';
import { KEYS, useConsoleMutation } from './mutation.ts';
import { useRequest } from './provider.tsx';
import { refuseSend, settleSend } from './reply-drafts.ts';
import { mergeThread, type Thread } from './thread.ts';

export interface ListConversationsQuery {
  candidateId?: string;
  searchId?: string;
  /** T36's chips (lists.ts `conversationFilterQuery`) — at the service, because the list is capped. */
  party?: PartyType;
  gate?: GateState;
  limit?: number;
}

export interface ListMessagesQuery {
  /** The previous page's `nextCursor`. Without it the thread comes back from the beginning. */
  after?: string;
  limit?: number;
}

export const conversationsApi = (request: RequestFn) => ({
  list: async (options: ListConversationsQuery = {}) =>
    (
      await request<{ conversations: ConversationSummary[] }>(
        `${API_PREFIX}/conversations${query({ ...options })}`,
      )
    ).conversations,
  get: (conversationId: string) =>
    request<ConversationSummary>(`${API_PREFIX}/conversations/${conversationId}`),
  listMessages: (conversationId: string, options: ListMessagesQuery = {}) =>
    request<MessagePage>(
      `${API_PREFIX}/conversations/${conversationId}/messages${query({ ...options })}`,
    ),
});

/**
 * Who a thread is with, by name (T288, 5 Oct 2026): on a client contact's thread the contact, even
 * when the same person is on file as a candidate too (T164), as the activity feed names them;
 * otherwise the candidate. Null when nobody on file owns the number.
 */
export function threadPartyName(
  thread: Pick<
    ConversationSummary,
    'partyType' | 'candidateName' | 'clientContactName' | 'staffName'
  >,
): string | null {
  if (thread.partyType === 'client_contact')
    return thread.clientContactName ?? thread.candidateName;
  if (thread.partyType === 'staff') return thread.staffName ?? thread.candidateName;
  return thread.candidateName;
}

export function useConversations(
  options: ListConversationsQuery = {},
): UseQueryResult<ConversationSummary[]> {
  const request = useRequest();
  return useQuery({
    queryKey: ['conversations', options],
    queryFn: () => conversationsApi(request).list(options),
    refetchInterval: POLL_MS.conversations,
  });
}

export function useConversation(conversationId: string): UseQueryResult<ConversationSummary> {
  const request = useRequest();
  return useQuery({
    queryKey: ['conversation', conversationId],
    queryFn: () => conversationsApi(request).get(conversationId),
    refetchInterval: POLL_MS.conversations,
  });
}

export function useThread(
  conversationId: string,
  /** T156: the demo candidate's thread, polled at `POLL_MS.demoThread`. */
  options: { fast?: boolean } = {},
): UseQueryResult<Thread> {
  const request = useRequest();
  const queryClient = useQueryClient();
  const key = ['thread', conversationId];

  return useQuery({
    queryKey: key,
    queryFn: async () => {
      const previous = queryClient.getQueryData<Thread>(key);
      const page = await conversationsApi(request).listMessages(conversationId, {
        after: previous?.cursor ?? undefined,
      });
      return mergeThread(previous, page);
    },
    // A thread longer than one page is turned through at once rather than one page per poll.
    refetchInterval: (q) =>
      q.state.data?.hasMore
        ? POLL_MS.threadCatchUp
        : options.fast
          ? POLL_MS.demoThread
          : POLL_MS.thread,
  });
}

// --- holds — pause and resume (T19, 3 Sep 2026) ---
//
// The one write on this screen that STOPS kadmon, and the only brain write the agent side reads
// as a gate: the worker checks `holds` before every send, at send time and not at enqueue time,
// so pausing catches a reply that is already queued (route-1 invariant 8). Nothing is patched
// into the cache on success — the mutation refetches, and the banner appears because the database
// says the thread is held, not because a button was pressed.

export const holdsApi = (request: RequestFn) => ({
  get: (conversationId: string) =>
    request<ThreadHoldState>(`${API_PREFIX}/conversations/${conversationId}/hold`),
  pause: (conversationId: string) =>
    request<{ id: string }>(`${API_PREFIX}/conversations/${conversationId}/hold`, {
      method: 'POST',
    }),
  resume: (conversationId: string) =>
    request<void>(`${API_PREFIX}/conversations/${conversationId}/hold`, { method: 'DELETE' }),
  /** T71: resume, and let kadmon's held reply out — one press, the service does both. */
  release: ({ conversationId, heldSendId }: ReleaseInput) =>
    request<{ id: string }>(`${API_PREFIX}/conversations/${conversationId}/hold/release`, {
      method: 'POST',
      body: { heldSendId },
    }),
  /** T128: send kadmon's held reply as the recruiter edited it — as hers, unsigned. */
  edit: ({ conversationId, heldSendId, body }: EditHeldInput) =>
    request<{ id: string }>(`${API_PREFIX}/conversations/${conversationId}/hold/edit`, {
      method: 'POST',
      body: { heldSendId, body },
    }),
  /** T128: drop kadmon's held reply; nothing is sent and a pause stays on. */
  discard: ({ conversationId, heldSendId }: ReleaseInput) =>
    request<{ id: string }>(`${API_PREFIX}/conversations/${conversationId}/hold/discard`, {
      method: 'POST',
      body: { heldSendId },
    }),
  /** T128a: have kadmon write her held reply again; the new one is held in its place. */
  redraft: ({ conversationId, heldSendId }: ReleaseInput) =>
    request<{ id: string }>(`${API_PREFIX}/conversations/${conversationId}/hold/redraft`, {
      method: 'POST',
      body: { heldSendId },
    }),
  /** T71: every thread somebody wrote into while kadmon was paused on it. */
  waiting: async () => (await request<WaitingResponse>(`${API_PREFIX}/waiting`)).threads,
});

export interface ReleaseInput {
  conversationId: string;
  /** The held outbox row, from the hold read or the waiting list. The words are the row's. */
  heldSendId: string;
}

/** T128: the held reply, and the recruiter's edit of it that goes out in its place. */
export interface EditHeldInput extends ReleaseInput {
  body: string;
}

/**
 * The pause state and the replies it has held, polled beside the thread.
 *
 * At the conversations interval rather than the thread's four seconds: a hold moves when a human
 * here presses something — and a write refetches it immediately — while a held reply is the
 * outbox reacting to that pause, which a recruiter reads rather than races.
 */
export function useThreadHold(conversationId: string): UseQueryResult<ThreadHoldState> {
  const request = useRequest();
  return useQuery({
    queryKey: ['hold', conversationId],
    queryFn: () => holdsApi(request).get(conversationId),
    refetchInterval: POLL_MS.conversations,
  });
}

/**
 * T166 (29 Sep 2026): kadmon asking the team to meet the thread's person, and what came of it —
 * polled beside the thread, at the conversations interval: it moves when a person here, or on
 * WhatsApp, answers it, and the answer refetches it at once.
 */
export const meetingAskApi = (request: RequestFn) => ({
  get: async (conversationId: string) =>
    (await request<MeetingAskResponse>(`${API_PREFIX}/conversations/${conversationId}/meeting`))
      .meeting,
  // T202 E-1: names the ask the card showed, so a newer ask is never decided by this press.
  decide: (conversationId: string, decision: 'yes' | 'no', askId: string) =>
    request<{ id: string }>(`${API_PREFIX}/conversations/${conversationId}/meeting`, {
      method: 'POST',
      body: { decision, askId },
    }),
});

export function useMeetingAsk(conversationId: string): UseQueryResult<MeetingAsk | null> {
  const request = useRequest();
  return useQuery({
    queryKey: ['meeting-ask', conversationId],
    queryFn: () => meetingAskApi(request).get(conversationId),
    refetchInterval: POLL_MS.conversations,
  });
}

/** T166: the thread's Yes and No. The worker records it and has her tell them in her words. */
export const useDecideMeeting = () =>
  useConsoleMutation(
    (request, args: { conversationId: string; decision: 'yes' | 'no'; askId: string }) =>
      meetingAskApi(request).decide(args.conversationId, args.decision, args.askId),
    KEYS.meetingAsk,
  );

/**
 * T177 (30 Sep 2026): kadmon's questions to the team on the thread, and what came of each — polled
 * beside the thread, at the conversations interval: an answer from here or from WhatsApp moves them.
 */
export const teamQuestionsApi = (request: RequestFn) => ({
  list: async (conversationId: string) =>
    (
      await request<TeamQuestionsResponse>(
        `${API_PREFIX}/conversations/${conversationId}/team-questions`,
      )
    ).questions,
  answer: (conversationId: string, questionId: string, answer: string) =>
    request<{ id: string }>(
      `${API_PREFIX}/conversations/${conversationId}/team-questions/${questionId}/answer`,
      { method: 'POST', body: { answer } },
    ),
});

export function useTeamQuestions(conversationId: string): UseQueryResult<TeamQuestion[]> {
  const request = useRequest();
  return useQuery({
    queryKey: ['team-questions', conversationId],
    queryFn: () => teamQuestionsApi(request).list(conversationId),
    refetchInterval: POLL_MS.conversations,
  });
}

/** T202 S-11: how long an answer may be, the service's `TEAM_ANSWER_MAX` (a test ties the two). */
export const TEAM_ANSWER_MAX = 2000;

/** T177: the thread's answer box. The worker records it and has her tell them in her words. */
export const useAnswerTeamQuestion = () =>
  useConsoleMutation(
    (request, args: { conversationId: string; questionId: string; answer: string }) =>
      teamQuestionsApi(request).answer(args.conversationId, args.questionId, args.answer),
    KEYS.teamQuestion,
  );

/** T156: the demo's Start, on the demo candidate's thread alone. */
export const demoApi = (request: RequestFn) => ({
  start: (conversationId: string) =>
    request<{ id: string }>(`${API_PREFIX}/conversations/${conversationId}/demo/start`, {
      method: 'POST',
    }),
});

/**
 * T156: Start demo. The request is filed at once; her message lands in the thread poll a few
 * seconds later, once the worker has had the model write it, and kadmon's answer after it.
 */
export const useStartDemo = () =>
  useConsoleMutation(
    (request, conversationId: string) => demoApi(request).start(conversationId),
    KEYS.demoStart,
  );

export const usePauseThread = () =>
  useConsoleMutation(
    (request, conversationId: string) => holdsApi(request).pause(conversationId),
    KEYS.hold,
  );

export const useResumeThread = () =>
  useConsoleMutation(
    (request, conversationId: string) => holdsApi(request).resume(conversationId),
    KEYS.hold,
  );

/**
 * Resume, and kadmon sends the reply she was holding (T71). Nothing is patched into the cache: the
 * request appears in the outbound poll as `pending`, then `sending`, and her bubble lands in the
 * thread poll once the worker has really sent it — as every send from the brain does.
 */
export const useReleaseHeldReply = () =>
  useConsoleMutation(
    (request, input: ReleaseInput) => holdsApi(request).release(input),
    KEYS.release,
  );

/** T128: her held reply sent as the recruiter edited it; it lands as a release does. */
export const useEditHeldReply = () =>
  useConsoleMutation(
    (request, input: EditHeldInput) => holdsApi(request).edit(input),
    KEYS.release,
  );

/** T128: her held reply dropped — the hold read stops listing it once the worker has closed it. */
export const useDiscardHeldReply = () =>
  useConsoleMutation(
    (request, input: ReleaseInput) => holdsApi(request).discard(input),
    KEYS.release,
  );

/**
 * T128a: her held reply written again. The request is filed at once; her new reply replaces the
 * old one on the hold poll once the worker has run her turn, and `redrafting` says so meanwhile.
 */
export const useRedraftHeldReply = () =>
  useConsoleMutation(
    (request, input: ReleaseInput) => holdsApi(request).redraft(input),
    KEYS.release,
  );

/**
 * Waiting on you (T71), polled at the conversations interval: the list moves when a candidate
 * writes or a recruiter acts, and both are things a person reads rather than races.
 */
export function useWaitingOnYou(): UseQueryResult<WaitingThread[]> {
  const request = useRequest();
  return useQuery({
    queryKey: ['waiting'],
    queryFn: () => holdsApi(request).waiting(),
    refetchInterval: POLL_MS.conversations,
  });
}

// --- outbound — the brain send lane (T19a, 3 Sep 2026) ---
//
// The narrowed lane (CLAUDE.md § Brain tier, 23 Aug 2026): a recruiter writes to a CANDIDATE
// while the 24-hour window is OPEN (a pause is no longer needed since T423; a send hands the thread
// back to kadmon). `send` writes a console-owned `outbound_requests` row and nothing else; the
// worker dispatches it into the outbox and sends it under the thread's lock, as kadmon and
// unsigned since T429 (services/worker/src/outbound.ts). So
// nothing is patched into the cache on success — the request shows up in the outbound poll as
// `pending`, then `sending`, and the message itself lands in the thread poll as a human bubble
// once it has really left. `cancel` withdraws a request the worker has not sent yet.

export interface SendInput {
  conversationId: string;
  /** What the recruiter typed. It goes out as written, unsigned (T429). */
  body: string;
}

export interface CancelInput {
  conversationId: string;
  requestId: string;
}

export const outboundApi = (request: RequestFn) => ({
  list: async (conversationId: string) =>
    (
      await request<OutboundRequestsResponse>(
        `${API_PREFIX}/conversations/${conversationId}/outbound`,
      )
    ).requests,
  send: ({ conversationId, body }: SendInput) =>
    request<{ id: string }>(`${API_PREFIX}/conversations/${conversationId}/outbound`, {
      method: 'POST',
      body: { body },
    }),
  cancel: ({ conversationId, requestId }: CancelInput) =>
    request<void>(`${API_PREFIX}/conversations/${conversationId}/outbound/${requestId}`, {
      method: 'DELETE',
    }),
});

/**
 * The requests on this thread and where each stands, polled at the THREAD's four seconds — it is
 * the same person watching the same screen for the same bubble, and a worker sweep is three.
 * `enabled` is the compose's own condition (a candidate thread, not opted out), so a client
 * contact's thread never asks.
 */
export function useOutboundRequests(
  conversationId: string,
  enabled: boolean,
): UseQueryResult<OutboundRequestRow[]> {
  const request = useRequest();
  return useQuery({
    queryKey: ['outbound', conversationId],
    queryFn: () => outboundApi(request).list(conversationId),
    enabled,
    refetchInterval: POLL_MS.thread,
  });
}

export const useSendMessage = () =>
  useConsoleMutation(
    (request, input: SendInput) => outboundApi(request).send(input),
    KEYS.outbound,
    // T296a: the box dropped the reply when it sent (it may be unmounted by now); a refused send
    // puts the text back, over nothing typed since. T296b: either answer settles the in-flight
    // text, and a refusal keeps its error for the box drawn when it lands.
    {
      onWritten: (input) => settleSend(input.conversationId),
      onError: (input, error) => refuseSend(input.conversationId, input.body, error),
    },
  );

// --- the follow-up template past the window (T78, 6 Oct 2026) ---

export type { FollowUpOffer };

export interface FollowUpInput {
  conversationId: string;
  topic: string;
}

export const followUpApi = (request: RequestFn) => ({
  offer: async (conversationId: string) =>
    (await request<FollowUpOfferResponse>(`${API_PREFIX}/conversations/${conversationId}/followup`))
      .offer,
  send: ({ conversationId, topic }: FollowUpInput) =>
    request<{ id: string }>(`${API_PREFIX}/conversations/${conversationId}/followup`, {
      method: 'POST',
      body: { topic },
    }),
});

/**
 * The follow-up the reply box offers, asked only while the window is closed (`enabled`): the name
 * and the topic it fills in, and whether Meta has approved the template yet. Polled at the thread's
 * pace, so an approval or a new topic shows without a reload.
 */
export function useFollowUpOffer(
  conversationId: string,
  enabled: boolean,
): UseQueryResult<FollowUpOffer | null> {
  const request = useRequest();
  return useQuery({
    queryKey: ['followup', conversationId],
    queryFn: () => followUpApi(request).offer(conversationId),
    enabled,
    refetchInterval: POLL_MS.thread,
  });
}

/** Send it: a request the worker sends as the template, shown in the thread like any send. */
export const useSendFollowUp = () =>
  useConsoleMutation(
    (request, input: FollowUpInput) => followUpApi(request).send(input),
    KEYS.outbound,
  );

export const useCancelSend = () =>
  useConsoleMutation(
    (request, input: CancelInput) => outboundApi(request).cancel(input),
    KEYS.outbound,
  );

// --- a message's file or voice note, downloaded (T107a, 23 Sep 2026) ---
//
// A READ, not a write: the brain API signs a half-hour URL for the stored bytes and the browser
// fetches them from the bucket itself (services/bff/src/files.ts). The DOWNLOAD asks at the press
// and invalidates nothing, because nothing changed; a photo, voice note or video SHOWN in the
// thread asks once as its bubble renders and renews before the URL lapses (`useMessageMedia`).

export interface MessageFileInput {
  conversationId: string;
  messageId: string;
  /** T557a: a PDF to show in a new tab rather than save. The API refuses anything else. */
  open?: boolean;
}

export const filesApi = (request: RequestFn) => ({
  url: ({ conversationId, messageId, open }: MessageFileInput) =>
    request<MessageFileResponse>(
      `${API_PREFIX}/conversations/${conversationId}/messages/${messageId}/file${open ? '?open=1' : ''}`,
    ),
});

export const useMessageFile = () =>
  useConsoleMutation((request, input: MessageFileInput) => filesApi(request).url(input), []);

/**
 * How often a shown photo, voice note or video asks for a fresh URL: five minutes inside the half
 * hour the service signs for (`FILE_URL_SECONDS`), as the meeting video does. A player that errors
 * before then asks at once (`screens/MessageMedia.tsx`).
 */
export const MEDIA_REFRESH_MS = 25 * 60 * 1000;

/**
 * The URL a bubble SHOWS its media from — WhatsApp's thread (23 Sep 2026): a photo drawn, a voice
 * note or a video played in place. A read on render rather than a press, one per media bubble,
 * cached by message and renewed before it lapses. Never logged, never drawn as a link.
 */
export function useMessageMedia(
  conversationId: string,
  messageId: string,
): UseQueryResult<MessageFileResponse> {
  const request = useRequest();
  return useQuery({
    queryKey: ['messageFile', conversationId, messageId],
    queryFn: () => filesApi(request).url({ conversationId, messageId }),
    refetchInterval: MEDIA_REFRESH_MS,
    staleTime: MEDIA_REFRESH_MS,
  });
}

// --- words held past the window (T431, item 26b) ---

export type { HeldWords };

export interface SendHeldInput {
  conversationId: string;
  requestId: string;
  /** The words as edited; absent sends them as held. */
  body?: string;
}

export const heldWordsApi = (request: RequestFn) => ({
  list: async (conversationId: string) =>
    (await request<HeldWordsResponse>(`${API_PREFIX}/conversations/${conversationId}/held-words`))
      .held,
  send: ({ conversationId, requestId, body }: SendHeldInput) =>
    request<{ id: string }>(
      `${API_PREFIX}/conversations/${conversationId}/held-words/${requestId}/send`,
      { method: 'POST', body: body === undefined ? {} : { body } },
    ),
});

/** The words kept for their author on this thread, polled at the thread's pace. */
export function useHeldWords(
  conversationId: string,
  enabled: boolean,
): UseQueryResult<HeldWords[]> {
  const request = useRequest();
  return useQuery({
    queryKey: ['held-words', conversationId],
    queryFn: () => heldWordsApi(request).list(conversationId),
    enabled,
    refetchInterval: POLL_MS.thread,
  });
}

/** Send held words (a message request; it pauses nothing). Drop is `useCancelSend`. */
export const useSendHeldWords = () =>
  useConsoleMutation(
    (request, input: SendHeldInput) => heldWordsApi(request).send(input),
    KEYS.outbound,
  );
